/**
 * feat-241 — the pinned total order (AC15).
 *
 * A four-state column has NO natural order, which is why the spec pins one
 * rather than leaving it to the implementer. Extracted as pure functions so the
 * order is asserted directly, without rendering a table.
 *
 * Descending, and this is also the readiness gradient the visual weight follows
 * (solid green → solid amber → solid grey → hollow):
 *
 *   completed → in_progress → available → locked
 *
 * within `in_progress`, by `lessons_complete / lessons_total` DESC, then
 * `lessons_complete` DESC. Ties break on crew name A–Z. Ascending reverses.
 *
 * ── ONE READING RECORDED, because AC15's sentence is ambiguous ───────────────
 * "ties break on crew name A–Z; ascending reverses" — I read `ascending
 * reverses` as inverting the READINESS GRADIENT (including the in-progress
 * sub-order), and `ties break on crew name A–Z` as an UNCONDITIONAL clause that
 * holds in both directions. So a descending sort and an ascending sort both list
 * names alphabetically WITHIN a state group; only the group order flips.
 *
 * The alternative reading — negate the whole comparator, so ascending ties break
 * Z–A — is simpler to implement but makes the name column jump around inside a
 * state group for no reason a manager could explain. If a reviewer prefers it,
 * it is one line: negate in `compareCrew` after the name tiebreak instead of
 * before. AC15's required mutation control (swapping `available` and `locked`)
 * goes red under either reading.
 */
import type {
  CoverageCell,
  CoverageCrewMember,
  CoverageTrack,
} from '../../../services/curriculumCoverageService';
import { applicableTrackCount, cellApplies, cellLessonsTotal } from './coverageApplies';
import type { CellStatus } from '../../../constants/curriculumCoverage';

/**
 * The readiness gradient as a number. HIGHER is more ready, so a descending sort
 * is "most ready first".
 *
 * The four values are deliberately spelled out rather than derived from an array
 * index: this map IS the AC15 order, and the mutation control swaps two entries
 * here.
 */
export const READINESS_RANK: Record<CellStatus, number> = {
  completed: 3,
  in_progress: 2,
  available: 1,
  locked: 0,
};

export type SortDirection = 'asc' | 'desc';

/**
 * Every sort kind this surface supports, as RUNTIME DATA.
 *
 * WHY THIS ARRAY EXISTS: QA F1 found that `location` and `tracks_complete` were
 * implemented in the `SortKey` union, in `sortCrew`, in `handleSort` and in the
 * unit tests — and emitted by NO control anywhere in the app. P9 records Preston
 * choosing "every column sortable" over the narrower "summary column only", so
 * two of the four kinds were silently missing from the product while every test
 * stayed green. The logic was tested; the affordance was not; nothing spanned the
 * gap.
 *
 * A TypeScript union is erased at runtime, so a test cannot enumerate it. This
 * array can be enumerated, and `feat241CoverageSortAffordance.test.tsx` walks it
 * asserting each kind is dispatchable FROM THE RENDERED DOM. Add a kind here
 * without adding a control and that test goes red.
 *
 * `SortKey` is derived from it below, so the two cannot drift.
 */
export const SORT_KINDS = ['name', 'location', 'tracks_complete', 'track'] as const;

export type SortKind = (typeof SORT_KINDS)[number];

/**
 * Which column is being sorted. A track column carries the track id, because
 * columns are data, not positions — reordering `tracks` must not silently
 * re-point the sort at a different track.
 */
export type SortKey =
  | { kind: 'name' }
  | { kind: 'location' }
  | { kind: 'tracks_complete' }
  | { kind: 'track'; trackId: string };

/**
 * Compile-time guard that `SortKey` and `SORT_KINDS` describe the same set. If
 * someone adds a variant to the union without adding it to the array (or the
 * reverse), this stops type-checking — so the runtime census above can never be
 * quietly narrower than the type.
 */
type _SortKindsCoverUnion = SortKey['kind'] extends SortKind ? true : never;
type _UnionCoversSortKinds = SortKind extends SortKey['kind'] ? true : never;
const _sortKindParity: [_SortKindsCoverUnion, _UnionCoversSortKinds] = [true, true];
void _sortKindParity;

/** Default landing order: crew name A–Z (AC15). */
export const DEFAULT_SORT: { key: SortKey; direction: SortDirection } = {
  key: { kind: 'name' },
  direction: 'asc',
};

/** Locale-aware, case-insensitive A–Z. `en` pinned so the order is not host-dependent. */
export function compareName(a: string, b: string): number {
  return a.localeCompare(b, 'en', { sensitivity: 'base' });
}

function cellFor(crew: CoverageCrewMember, trackId: string): CoverageCell | undefined {
  return crew.cells.find((c) => c.track_id === trackId);
}

/**
 * The in-progress sub-order: fraction of the track completed, DESC.
 *
 * `lessons_total` comes from the TRACK (P14). Zero-lesson tracks are `available`
 * by slice 3's semantics and never `in_progress`, so the guard below is a
 * divide-by-zero belt, not a state we expect to see.
 */
function progressRatio(cell: CoverageCell, lessonsTotal: number): number {
  if (!lessonsTotal) return 0;
  return cell.lessons_complete / lessonsTotal;
}

/**
 * Compare two crew members on ONE track column, most-ready first. Returns 0 when
 * they are indistinguishable on this column — the caller applies the name
 * tiebreak.
 */
export function compareOnTrack(
  a: CoverageCrewMember,
  b: CoverageCrewMember,
  track: CoverageTrack,
): number {
  const ca = cellFor(a, track.id);
  const cb = cellFor(b, track.id);
  // A missing cell is a backend defect (`cells` is dense by contract), not a
  // state. Sort it last rather than inventing a status for it.
  if (!ca && !cb) return 0;
  if (!ca) return 1;
  if (!cb) return -1;
  // feat-302: a track off-curriculum for someone has no readiness to rank.
  // It sorts after every real state (sortCrew keeps it last in BOTH directions).
  const aa = cellApplies(ca);
  const ab = cellApplies(cb);
  if (!aa && !ab) return 0;
  if (!aa) return 1;
  if (!ab) return -1;

  const rankDelta = READINESS_RANK[cb.status] - READINESS_RANK[ca.status];
  if (rankDelta !== 0) return rankDelta;

  if (ca.status === 'in_progress' && cb.status === 'in_progress') {
    const ratioDelta =
      progressRatio(cb, cellLessonsTotal(cb, track)) - progressRatio(ca, cellLessonsTotal(ca, track));
    if (ratioDelta !== 0) return ratioDelta;
    const completeDelta = cb.lessons_complete - ca.lessons_complete;
    if (completeDelta !== 0) return completeDelta;
  }
  return 0;
}

/** feat-302 D1 — tracks complete over the tracks that apply to this person. */
function completionRatio(member: CoverageCrewMember): number {
  const applicable = applicableTrackCount(member);
  return applicable > 0 ? member.tracks_complete / applicable : 0;
}

/**
 * The whole comparator. `direction` flips the readiness gradient; the crew-name
 * tiebreak stays A–Z in both directions (see the header note).
 */
export function sortCrew(
  crew: readonly CoverageCrewMember[],
  tracks: readonly CoverageTrack[],
  key: SortKey,
  direction: SortDirection,
): CoverageCrewMember[] {
  const flip = direction === 'desc' ? 1 : -1;
  const track = key.kind === 'track' ? tracks.find((t) => t.id === key.trackId) : undefined;

  const primary = (a: CoverageCrewMember, b: CoverageCrewMember): number => {
    switch (key.kind) {
      case 'name':
        // The name column's own "descending" is Z–A, which IS the reversal of
        // its A–Z. There is no separate tiebreak to preserve here.
        return -flip * compareName(a.name, b.name);
      case 'location':
        return -flip * compareName(a.location_name, b.location_name);
      case 'tracks_complete': {
        // feat-302 D1: by each person's OWN ratio (tracks complete / tracks that
        // apply to them), so "2 of 2" outranks "3 of 7"; then by the raw count.
        const ratioDelta = completionRatio(b) - completionRatio(a);
        if (ratioDelta !== 0) return flip * ratioDelta;
        return flip * (b.tracks_complete - a.tracks_complete);
      }
      case 'track':
        return track ? flip * compareOnTrack(a, b, track) : 0;
      default:
        return 0;
    }
  };

  /** feat-302: on a track column, crew it does not apply to go LAST whichever
   *  way the column is sorted — they are not part of the readiness order. */
  const notApplicableLast = (a: CoverageCrewMember, b: CoverageCrewMember): number => {
    if (!track) return 0;
    const ca = cellFor(a, track.id);
    const cb = cellFor(b, track.id);
    const na = !!ca && !cellApplies(ca);
    const nb = !!cb && !cellApplies(cb);
    if (na === nb) return 0;
    return na ? 1 : -1;
  };

  return [...crew].sort((a, b) => {
    const n = notApplicableLast(a, b);
    if (n !== 0) return n;
    const p = primary(a, b);
    if (p !== 0) return p;
    // Unconditional A–Z tiebreak — never reversed.
    return compareName(a.name, b.name);
  });
}
